// @noflow
import { useNotifications } from '@/context/notifications/notifications'
import { ProductUpsellContext } from '@/context/productUpsellModal/productUpsellModal'
import {
  Meal,
  RecipeSurchargeCalculation,
  SaveFreshMealsMutation,
  SaveFreshMealsMutationVariables
} from '@/gql/graphql'
import { ACCOUNT_ROUTES } from '@/routes'
import { useMutation, useQuery, useReactiveVar } from '@apollo/client'
import { Grid } from '@mui/material'
import * as Sentry from '@sentry/browser'
import camelCase from 'lodash/camelCase'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import isUndefined from 'lodash/isUndefined'
import times from 'lodash/times'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'

import { dogsDataVar, featureFlagsDataVar } from '@/services/apollo'

// Constants
import BREAKPOINTS from '@/constants/Breakpoints'
import { UPDATED_RECIPE_MIX } from '@/constants/RibbonEvents'

// Hooks
import { useGoBack } from '@/hooks/useGoBack/useGoBack'
import { useModal } from '@/hooks/useModal'
import useWindowSize from '@/hooks/useWindowSize'

import RecipesProgressBar from './components/RecipesProgressBar/RecipesProgressBar'
// Components
import AlertCard from '@/components/elements/atoms/Alert/AlertCard'
import { Button } from '@/components/elements/atoms/Button'
import Card from '@/components/elements/atoms/Card/Card'
import { CardSkeleton } from '@/components/elements/atoms/Card/CardSkeleton'
import Image from '@/components/elements/atoms/Image'
import Notification from '@/components/elements/atoms/InPageNotification/InPageNotification'
import Tabs, { Tab } from '@/components/elements/atoms/Tabs/Tabs'
import Text from '@/components/elements/atoms/Text/Text'
import { Header } from '@/components/elements/molecules/Header'
import ConfirmationModal, {
  modalData
} from '@/components/elements/organisms/ConfirmationModal/ConfirmationModal'
import { ProductUpsellAnalyticTypes } from '@/components/elements/organisms/ProductsUpsellModal/ProductsUpsellModal'
import { EDIT_PLAN_PAGE_QUERY } from '@/components/pages/EditPlanPage/queries/queries'
import { RecipeCard } from '@/components/pages/EditRecipes/components/RecipeCard/RecipeCard'

import STYLES from './EditRecipes.module.sass'

import { SAVE_FRESH_MEALS_MUTATION } from './mutations/saveFreshMealsMutation'
import { MEAL_FLAVOURS_QUERY } from './queries/mealFlavoursQuery'
import { SAVE_FRESH_MEALS_UPDATE_QUERY } from './queries/saveFreshMealsUpdateQuery'

import { MealInput } from '@/types'

import { PlusBadge } from './icons/plusBadge'
import { TickBadge } from './icons/tickBadge'
import { getAllergenSlugs, sortAndFilterRecipes } from './utils'

type MealInputKey = keyof MealInput

type EditFreshMealsContext = {
  mealQuantities: MealInput
  percentFilled: number
  increaseMealQuantity: (mealInputKey: MealInputKey, amount: number) => void
  decreaseMealQuantity: (mealInputKey: MealInputKey, amount: number) => void
  pouchSize: number
  pouchesPerServing: number
}

const namespace = 'account'

const PageHeader = ({
  handleBack,
  isSmallScreen
}: {
  handleBack: () => void
  isSmallScreen: boolean
}) => {
  return (
    <Header
      variant="secondary"
      title={{
        text: 'edit_recipes.fresh_meals_page.fresh_meals',
        variant: isSmallScreen ? 'display28' : 'display45',
        shouldScale: false,
        namespace
      }}
      leftAdornment={{
        onPress: handleBack
      }}
    />
  )
}

const EditFreshMealsContext = createContext<EditFreshMealsContext>(
  {} as EditFreshMealsContext
)

const getTotalPouchesAdded = (mealQuantities: MealInput): number => {
  return Object.values(mealQuantities).reduce(
    (acc, quantity) => (acc += quantity),
    0
  )
}

const TABS = {
  inTheBox: 'inTheBox',
  moreRecipes: 'moreRecipes'
}

const EditRecipes = (): JSX.Element => {
  const { openModal } = useModal('recipes-make-room-for-more')

  const { openModal: openNotSavedModal } = useModal(
    'edit-recipes-not-saved-modal'
  )

  const goBack = useGoBack()
  const { windowWidth } = useWindowSize()
  const { t } = useTranslation('account')
  const isSmallScreen = windowWidth < BREAKPOINTS.sm
  const { setErrorNotification } = useNotifications()
  const { dogs, pronoun } = useReactiveVar(dogsDataVar) || {}
  const [noChanges, setNoChanges] = useState(true)
  const featureFlagData = useReactiveVar(featureFlagsDataVar)
  const shouldSeeSegmentedPausedPage =
    featureFlagData?.shouldSeeSegmentedPausedPage === 'variant'
  const { loading, data, error } = useQuery(MEAL_FLAVOURS_QUERY, {
    variables: {
      num: 1,
      calculationType: RecipeSurchargeCalculation.per_pouch
    }
  })
  const userId = data?.user.id || ''
  const { openProductsUpsellModal } = useContext(ProductUpsellContext)

  const [saveFreshMeals, { loading: saveLoading, error: saveError }] =
    useMutation<SaveFreshMealsMutation, SaveFreshMealsMutationVariables>(
      SAVE_FRESH_MEALS_MUTATION,
      {
        onCompleted: () => {
          modalData({
            isOpen: true,
            text: 'edit_recipes.confirmation_modal',
            namespace,
            delay: 1000
          })
          setTimeout((): void => {
            const showUpsellModal =
              (data?.user.shippingCountry.showUpcomingOrderSeeMoreExtras &&
                data?.user.subscription.nextNBoxes[0]?.isNextEditableBox &&
                !shouldSeeSegmentedPausedPage) ||
              false

            if (showUpsellModal) {
              openProductsUpsellModal({
                headerText: {
                  text: 'products_upsell_modal.recipes_changed_modal.title'
                },
                analyticsType: ProductUpsellAnalyticTypes.recipeChange
              })
            }
            goBack(ACCOUNT_ROUTES.base)
          }, 2000)
        },
        onError: () => {
          setErrorNotification({
            text: 'edit_recipes.notification.save_meals_failed',
            namespace
          })
          if (saveError) {
            Sentry.captureException(
              `Could not update meals from SAVE_FRESH_MEALS_MUTATION`,
              { extra: { error: saveError.message } }
            )
          }
        },
        refetchQueries: [
          {
            query: SAVE_FRESH_MEALS_UPDATE_QUERY
          },
          {
            query: EDIT_PLAN_PAGE_QUERY
          }
        ]
      }
    )

  const [mealQuantities, setMealQuantities] = useState<MealInput>({})
  const [initialised, setInitialised] = useState(false)

  const pouchSize = data?.user.subscription.plan?.pouchSize ?? 0

  const numberOfPouches = data?.user.subscription.plan?.numberOfPouches ?? 0

  const pouchesPerServing =
    data?.user.subscription.plan?.servingInformation?.pouchesPerServing ?? 0

  const freshMeals = useMemo(() => {
    return data?.user.subscription.meal
  }, [data])

  const totalPouchesAdded = useMemo(() => {
    return getTotalPouchesAdded(mealQuantities)
  }, [mealQuantities])

  const percentFilled = useMemo(() => {
    return (totalPouchesAdded / numberOfPouches) * 100
  }, [totalPouchesAdded, numberOfPouches])

  const changeMealQuantities = useCallback(
    (quantities) => setMealQuantities(quantities),
    []
  )

  const increaseMealQuantity = useCallback(
    (mealInputKey: MealInputKey, amount = 1): void => {
      if (noChanges) {
        setNoChanges(false)
      }

      setMealQuantities((prevMealQuantities) => {
        const newQuantities = {
          ...prevMealQuantities,
          [mealInputKey]: (prevMealQuantities[mealInputKey] || 0) + amount
        }

        const newTotalPouches = getTotalPouchesAdded(newQuantities)

        if (newTotalPouches > numberOfPouches) {
          openModal({
            freshMeals,
            dogs: data?.user?.dogs,
            mealQuantities: prevMealQuantities,
            onUpdate: changeMealQuantities,
            numberOfPouches
          })

          return prevMealQuantities
        }

        return newQuantities
      })
    },
    [
      numberOfPouches,
      noChanges,
      openModal,
      freshMeals,
      data?.user?.dogs,
      changeMealQuantities
    ]
  )

  const decreaseMealQuantity = useCallback(
    (mealInputKey: MealInputKey, amount = 1): void => {
      if (noChanges) {
        setNoChanges(false)
      }

      setMealQuantities((prevMealQuantities) => ({
        ...prevMealQuantities,
        [mealInputKey]: Math.max(
          0,
          (prevMealQuantities[mealInputKey] || 0) - amount
        )
      }))
    },
    [noChanges]
  )

  const extraOrMissingPouches = useMemo(() => {
    return totalPouchesAdded - numberOfPouches
  }, [totalPouchesAdded, numberOfPouches])

  const handleSave = useCallback(async (): Promise<void> => {
    window.ribbon && window.ribbon('trigger', UPDATED_RECIPE_MIX)
    if (extraOrMissingPouches === 0) {
      await saveFreshMeals({
        variables: {
          userId,
          meal: mealQuantities
        }
      })
    }
  }, [extraOrMissingPouches, mealQuantities, saveFreshMeals, userId])

  const handleBack = useCallback((): void => {
    if (noChanges) {
      goBack(ACCOUNT_ROUTES.base)
    } else {
      const data = {
        onCancel: () => goBack(ACCOUNT_ROUTES.base),
        onSave: handleSave
      }

      openNotSavedModal(data)
    }
  }, [goBack, handleSave, noChanges, openNotSavedModal])

  const value = useMemo(() => {
    const valueObj = {
      mealQuantities,
      percentFilled,
      increaseMealQuantity,
      decreaseMealQuantity,
      pouchSize,
      pouchesPerServing
    }
    return valueObj
  }, [
    mealQuantities,
    percentFilled,
    increaseMealQuantity,
    decreaseMealQuantity,
    pouchSize,
    pouchesPerServing
  ])

  useEffect(() => {
    if (
      !initialised &&
      data?.user.subscription.meal &&
      data.user.subscription.meal.length > 0 &&
      data?.user.subscription.plan
    ) {
      const mealQuantitiesFromData = data.user.subscription.meal.reduce(
        (acc: MealInput, meal: Meal) => {
          const mealInputKey = camelCase(meal.slug) as MealInputKey
          acc[mealInputKey] = meal.quantity
          return acc
        },
        {} as MealInput
      )
      setMealQuantities(mealQuantitiesFromData)
      setInitialised(true)
    }
  }, [data, initialised])

  if (loading) {
    return (
      <Grid container className={STYLES.contentWrapper}>
        <PageHeader isSmallScreen={isSmallScreen} handleBack={handleBack} />

        <Grid
          container
          item
          rowSpacing={{ xs: 2, sm: 3 }}
          paddingLeft={2}
          paddingRight={2}
        >
          {times(12).map(
            (index: number): JSX.Element => (
              <Grid item xs={12} key={index}>
                <CardSkeleton height={isSmallScreen ? '13.6rem' : '15rem'} />
              </Grid>
            )
          )}
        </Grid>
      </Grid>
    )
  }
  if (error || !data || !freshMeals) {
    if (error) {
      Sentry.captureException(`Error in MEAL_FLAVOURS_QUERY`, {
        extra: { error: error.message }
      })
    }
    if (!data || !freshMeals) {
      Sentry.captureException(
        'Tried to render EditRecipes without data or freshMeals'
      )
    }
    // TODO: check if there is a holistic SPA approach for error state UI
    return (
      <div className={STYLES.alertContainer}>
        <AlertCard
          message={{
            text: 'edit_recipes.error_message',
            namespace
          }}
          variant="error"
        />
      </div>
    )
  }
  const boxIsFull = extraOrMissingPouches === 0
  const boxIsOver = extraOrMissingPouches > 0

  const recipesInTheBox = sortAndFilterRecipes(freshMeals, data?.user?.dogs)

  const allergenSlugs = getAllergenSlugs(data?.user?.dogs)

  const showMoreRecipes = recipesInTheBox.length !== freshMeals.length

  const recipesNotInTheBox = freshMeals?.filter(
    (freshMeal: Meal) => freshMeal.quantity === 0
  )

  const topPicksRecipes =
    recipesNotInTheBox?.length >= 6
      ? recipesNotInTheBox
          ?.filter(
            (freshMeal: Meal) =>
              !allergenSlugs?.includes(freshMeal.flavour.slug)
          )
          ?.sort((a: Meal, b: Meal) => {
            return (
              a.flavour.flavourPriority.priority -
              b.flavour.flavourPriority.priority
            )
          })
          .slice(0, 3)
      : null

  const topPickSlugs = topPicksRecipes?.map(
    (freshMeal: Meal) => freshMeal.flavour.slug
  )

  const remainingRecipes =
    recipesNotInTheBox?.length >= 6
      ? recipesNotInTheBox
          ?.filter(
            (freshMeal: Meal) => !topPickSlugs?.includes(freshMeal.flavour.slug)
          )
          ?.sort((a: Meal, b: Meal) => {
            return (
              (allergenSlugs?.includes(a.flavour.slug) ? 1 : 0) -
              (allergenSlugs?.includes(b.flavour.slug) ? 1 : 0)
            )
          })
      : recipesNotInTheBox?.sort((a: Meal, b: Meal) => {
          return (
            (allergenSlugs?.includes(a.flavour.slug) ? 1 : 0) -
            (allergenSlugs?.includes(b.flavour.slug) ? 1 : 0)
          )
        })

  if (isNil(dogs) || isUndefined(pronoun))
    return (
      <div className={STYLES.recipesWrapper}>
        <CardSkeleton height={600} />
      </div>
    )

  const isNextBoxLocked =
    !!data &&
    !isEmpty(data.user.subscription.nextNBoxes) &&
    !data.user.subscription.nextNBoxes[0]?.amendable

  return (
    <EditFreshMealsContext.Provider value={value}>
      <Grid
        container
        className={STYLES.contentWrapper}
        paddingLeft={2}
        paddingRight={2}
      >
        <PageHeader isSmallScreen={isSmallScreen} handleBack={handleBack} />

        <div className={STYLES.recipesWrapper}>
          {isNextBoxLocked && (
            <div className={STYLES.boxLocked}>
              <Notification
                text={{
                  text: 'edit_recipes.fresh_meals_page.box_is_locked_tooltip',
                  namespace
                }}
                variant="info"
              />
            </div>
          )}

          <Tabs initial={TABS.inTheBox} variant="withIcons">
            <Tab
              label={{
                text: 'edit_recipes.fresh_meals_page.in_your_box',
                namespace
              }}
              id={TABS.inTheBox}
              Icon={TickBadge}
              iconAlt={t('edit_recipes.fresh_meals_page.alts.box')}
            >
              <Card
                variant="brandYellow100"
                padding={0}
                squared={isSmallScreen}
                mask
              >
                <Grid
                  container
                  item
                  rowSpacing={{ xs: 2, sm: 3 }}
                  flexDirection="column"
                  className={STYLES.cardWrapper}
                  marginBottom={16}
                >
                  {recipesInTheBox?.map((freshMeal: Meal) => (
                    <div key={freshMeal.slug} className={STYLES.rowCard}>
                      <RecipeCard
                        identifier="in-the-box"
                        freshMeal={freshMeal}
                        showBenefits={false}
                        hasAllergies={allergenSlugs.includes(freshMeal.slug)}
                        dogName={dogs[0]?.name}
                        dogCount={dogs?.length}
                      />
                    </div>
                  ))}
                </Grid>
              </Card>
            </Tab>
            {showMoreRecipes ? (
              <Tab
                label={{
                  text: 'edit_recipes.fresh_meals_page.more_recipes',
                  namespace
                }}
                id={TABS.moreRecipes}
                Icon={PlusBadge}
                iconAlt={t('edit_recipes.fresh_meals_page.alts.pouches')}
              >
                {topPicksRecipes?.length > 0 ? (
                  <Card
                    variant="brandYellow200"
                    padding={0}
                    squared={isSmallScreen}
                    mask
                    className={STYLES.topPicksCard}
                  >
                    <div className={STYLES.topPicksContent}>
                      <Image
                        className={STYLES.broccoli}
                        slug={'broccoli-floret'}
                        image={{
                          retina: true,
                          height: 63,
                          width: 47
                        }}
                        alt={t('edit_recipes.fresh_meals_page.alts.broccoli')}
                      />
                      <Image
                        className={STYLES.carrots}
                        slug={'carrot-slices'}
                        image={{
                          retina: true,
                          height: 37,
                          width: 60
                        }}
                        alt={t('edit_recipes.fresh_meals_page.alts.carrots')}
                      />
                      <Image
                        className={STYLES.beans}
                        slug={'green-beans'}
                        image={{
                          retina: true,
                          height: 121,
                          width: 148
                        }}
                        alt={t('edit_recipes.fresh_meals_page.alts.beans')}
                      />

                      <Grid
                        container
                        item
                        rowSpacing={{ xs: 2, sm: 3 }}
                        flexDirection="column"
                        className={STYLES.topPicksCardWrapper}
                      >
                        <div className={STYLES.topPicksCopy}>
                          <Text
                            text={`edit_recipes.fresh_meals_page.recommended_recipes.title`}
                            namespace={namespace}
                            variables={{
                              dogName: dogs[0]?.name,
                              count: dogs?.length
                            }}
                            margin={false}
                            variant="display20"
                          />
                          <Text
                            text={`edit_recipes.fresh_meals_page.recommended_recipes.subtitle_recipe${
                              topPicksRecipes?.length === 1 ? '' : 's'
                            }`}
                            namespace={namespace}
                            variables={{
                              dogName: dogs[0]?.name,
                              context: pronoun,
                              count: topPicksRecipes?.length
                            }}
                            margin={false}
                          />
                        </div>
                        {topPicksRecipes?.map((freshMeal: Meal) => (
                          <div key={freshMeal.slug} className={STYLES.rowCard}>
                            <RecipeCard
                              identifier="top-picks"
                              freshMeal={freshMeal}
                              showBenefits
                              topPick
                            />
                          </div>
                        ))}
                      </Grid>
                    </div>
                  </Card>
                ) : (
                  <div />
                )}
                <Card
                  variant="brandYellow100"
                  padding={0}
                  squared={isSmallScreen}
                  mask
                >
                  <Grid
                    container
                    item
                    rowSpacing={{ xs: 2, sm: 3 }}
                    flexDirection="column"
                    className={STYLES.cardWrapper}
                  >
                    {remainingRecipes?.map((freshMeal: Meal) => (
                      <div key={freshMeal.slug} className={STYLES.rowCard}>
                        <RecipeCard
                          identifier="more-recipes"
                          freshMeal={freshMeal}
                          showBenefits={false}
                          hasAllergies={allergenSlugs.includes(freshMeal.slug)}
                          dogName={dogs[0]?.name}
                          dogCount={dogs?.length}
                        />
                      </div>
                    ))}
                  </Grid>
                </Card>
              </Tab>
            ) : (
              <div />
            )}
          </Tabs>
        </div>
        <div className={STYLES.stickyFooter}>
          <div className={STYLES.stickyFooterContent}>
            <Text
              text="edit_recipes.fresh_meals_page.total_pouches"
              namespace={namespace}
              bold
              variables={{ pouchSize: pouchSize }}
              margin={false}
            />

            <RecipesProgressBar
              percentFilled={percentFilled}
              boxIsFull={boxIsFull}
              boxIsOver={boxIsOver}
              totalPouchesAdded={totalPouchesAdded}
              numberOfPouches={numberOfPouches}
            />

            <div className={STYLES.ctaWrapper}>
              <Button
                identifier="edit_recipes.fresh_meals_page.update_meals"
                onClick={handleSave}
                typography={{
                  namespace,
                  text: saveLoading
                    ? 'edit_recipes.fresh_meals_page.updating'
                    : 'edit_recipes.fresh_meals_page.update_meals'
                }}
                variant="primary"
                disabled={noChanges || percentFilled !== 100 || saveLoading}
                fullWidth
              />
            </div>
          </div>
        </div>
      </Grid>
      <ConfirmationModal />
    </EditFreshMealsContext.Provider>
  )
}

export type { MealInputKey }
export { EditRecipes, EditFreshMealsContext }
